import type { JSONContent } from '@tiptap/react';
import { z } from 'zod';

import type { Note } from './model';
import type { Change, RecordLike } from './productivity';

export const assetSchema = z.object({
  name: z.string().max(2000),
  mime: z.enum(['image/png', 'image/jpeg', 'image/webp', 'image/gif']),
  data: z
    .string()
    .max(7_000_000)
    .regex(/^data:image\/(png|jpeg|webp|gif);base64,[A-Za-z0-9+/]+={0,2}$/),
  bytes: z
    .number()
    .int()
    .min(1)
    .max(5 * 1024 * 1024),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
});

export type Asset = z.infer<typeof assetSchema>;

export async function assetFor(data: string, name: string): Promise<Asset> {
  const match = /^data:(image\/[a-z]+);base64,(.+)$/.exec(data);

  if (!match) {
    throw new Error('Unsupported embedded image');
  }

  const bytes = Uint8Array.from(atob(match[2]), char => char.charCodeAt(0));
  const hash = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));

  return assetSchema.parse({
    name,
    mime: match[1],
    data,
    bytes: bytes.length,
    sha256: Array.from(hash, b => b.toString(16).padStart(2, '0')).join(''),
  });
}

export function imageAssetId(node: JSONContent) {
  const src = node.type === 'image' ? String(node.attrs?.src ?? '') : '';

  return src.startsWith('cairn-asset:') ? src.slice(12) : undefined;
}

export function referencedAssets(node: JSONContent): string[] {
  const id = imageAssetId(node);

  return [...(id ? [id] : []), ...(node.content ?? []).flatMap(referencedAssets)];
}

export async function extractImages(records: RecordLike[], changes: Change[]): Promise<Change[]> {
  const assets = [
    ...records.filter(r => r.kind === 'asset'),
    ...changes.filter(r => r.kind === 'asset'),
  ];

  const added: Change[] = [];

  async function convert(node: JSONContent): Promise<JSONContent> {
    if (node.type === 'image' && String(node.attrs?.src ?? '').startsWith('data:')) {
      const asset = await assetFor(String(node.attrs!.src), String(node.attrs?.alt ?? 'Image'));

      let record = [...assets, ...added].find(
        r => (r.value as Asset).sha256 === asset.sha256 && (r.value as Asset).mime === asset.mime,
      );

      if (!record) {
        record = { id: crypto.randomUUID(), kind: 'asset', value: asset };
        added.push(record);
      }

      return { ...node, attrs: { ...node.attrs, src: `cairn-asset:${record.id}` } };
    }

    return node.content ? { ...node, content: await Promise.all(node.content.map(convert)) } : node;
  }

  const converted: Change[] = [];

  // Preserve deterministic deduplication within each atomic edit.
  for (const change of changes) {
    converted.push(
      change.kind === 'note'
        ? {
            ...change,
            value: {
              ...(change.value as Note),
              content: await convert((change.value as Note).content),
            },
          }
        : change,
    );
  }

  return [...added, ...converted];
}

export function resolveImages(node: JSONContent, records: RecordLike[]): JSONContent {
  const id = imageAssetId(node),
    record = id ? records.find(r => r.kind === 'asset' && r.id === id) : undefined;

  return {
    ...node,
    ...(record ? { attrs: { ...node.attrs, src: (record.value as Asset).data } } : {}),
    ...(node.content ? { content: node.content.map(n => resolveImages(n, records)) } : {}),
  };
}
